import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../admin/api";

/** Venue address/map */
const ADDRESS_TEXT = "Bruderstraße 58, 59494 Soest";
const MAP_EMBED_SRC =
  "https://www.google.com/maps?q=" +
  encodeURIComponent(ADDRESS_TEXT) +
  "&output=embed";

/** Labels */
const DAY_LABELS = {
  enFull: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
  deFull: [
    "Sonntag",
    "Montag",
    "Dienstag",
    "Mittwoch",
    "Donnerstag",
    "Freitag",
    "Samstag",
  ],
};

export default function OpeningHoursPublic() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("de") ? "de" : "en";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    api
      .get("/api/public/opening-hours")
      .then((res) =>
        mounted && setRows(Array.isArray(res.data) ? res.data : [])
      )
      .catch((e) =>
        setErr(e?.response?.data?.error || "Failed to load opening hours")
      )
      .finally(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  // Status pill
  const status = useMemo(() => {
    if (!rows?.length) return { text: "", isOpen: false };
    const now = new Date(
      new Date().toLocaleString("en-US", { timeZone: "Europe/Berlin" })
    );
    const today = now.getDay();
    const curMins = now.getHours() * 60 + now.getMinutes();
    const toMin = (hhmm) => {
      const [h, m] = hhmm.split(":").map(Number);
      return h * 60 + m;
    };

    const todayRow =
      rows.find((r) => r.weekday === today) || { isClosed: true, intervals: [] };
    let openNow = false;

    if (!todayRow.isClosed && todayRow.intervals.length) {
      for (const iv of todayRow.intervals) {
        const o = toMin(iv.open),
          c = toMin(iv.close);
        if (o < c ? curMins >= o && curMins < c : curMins >= o || curMins < c)
          openNow = true;
      }
    }

    return {
      text: openNow
        ? lang === "de"
          ? "Geöffnet"
          : "Open now"
        : lang === "de"
        ? "Geschlossen"
        : "Closed",
      isOpen: openNow,
    };
  }, [rows, lang]);

  // Helper to get formatted times for specific days
  const getTimeForDays = (days) => {
    if (!rows?.length) return [];
    const getTimes = (weekday) => {
      const r = rows.find((x) => x.weekday === weekday);
      if (!r) return [];
      if (r.isClosed) return [lang === "de" ? "Geschlossen" : "Closed"];
      return r.intervals.map((iv) => `${iv.open} – ${iv.close}`);
    };

    if (Array.isArray(days)) {
      const firstDay = getTimes(days[0]);
      const allSame = days.every(
        (d) => JSON.stringify(getTimes(d)) === JSON.stringify(firstDay)
      );
      return allSame ? firstDay : days.flatMap(getTimes);
    } else {
      return getTimes(days);
    }
  };

  // ✅ Dynamic localized labels
  const DAYS_DISPLAY = lang === "de"
    ? [
        { label: "Montag", days: [1] },
        { label: "Dienstag", days: [2] },
        { label: "Mittwoch–Freitag", days: [3, 4, 5] },
        { label: "Samstag", days: [6] },
        { label: "Sonntag", days: [0] },
      ]
    : [
        { label: "Monday", days: [1] },
        { label: "Tuesday", days: [2] },
        { label: "Wednesday–Friday", days: [3, 4, 5] },
        { label: "Saturday", days: [6] },
        { label: "Sunday", days: [0] },
      ];

  return (
    <section className="py-12 sm:py-16 md:py-20" id="visit">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Map */}
          <div className="h-full flex flex-col justify-start">
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl mb-2">
              {lang === "de" ? "Wegbeschreibung" : "Directions"}
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mb-4" />
            <div className="rounded-2xl overflow-hidden shadow h-full">
              <iframe
                title="map"
                src={MAP_EMBED_SRC}
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <p className="mt-2 text-sm sm:text-base">{ADDRESS_TEXT}</p>
          </div>

          {/* Opening Hours */}
          <div className="h-full flex flex-col justify-start">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl">
                {lang === "de" ? "Öffnungszeiten" : "Opening Hours"}
              </h2>
              {!loading && !err && (
                <span
                  className={`text-2xl font-bold ${
                    status.isOpen ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {status.text}
                </span>
              )}
            </div>
            <div className="w-24 h-1 bg-yellow-500 mb-4" />

            {/* ✅ Exact layout with translation support */}
            <div className="rounded-2xl border divide-y divide-gray-300 p-4 text-base leading-relaxed">
              {DAYS_DISPLAY.map(({ label, days }, idx) => {
                const times = getTimeForDays(days);
                return (
                  <div key={idx} className="py-2">
                    <div className="font-medium text-gray-800">{label} :</div>
                    <div className="pl-6 text-gray-700">
                      {times.map((t, i) => (
                        <div key={i}>{t}</div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
